import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { modificationEnCours, panneauDocument, versionsDesDocuments } from './documentLive.js';

// Documents vivants de la conversation : versions déduites des messages, section sélectionnée
// (puce du compositeur), ouverture du panneau pendant une réponse qui modifie un document.

const LiveDocumentContext = createContext(null);
export const useLiveDocument = () => useContext(LiveDocumentContext);
export const LiveDocumentProvider = LiveDocumentContext.Provider;

export function useLiveDocumentController({ messages, openArtifact, openId, wide, streaming }) {
  const documents = useMemo(() => versionsDesDocuments(messages), [messages]);
  const enCours = useMemo(() => modificationEnCours(messages), [messages]);
  const [selection, selectionner] = useState(null);

  const ouvrir = (documentId) => {
    const dernier = documents.get(documentId)?.at(-1);
    if (!dernier) return;
    openArtifact?.({
      id: panneauDocument(documentId),
      type: 'document',
      title: dernier.spec.title || 'Document',
      subtitle: null,
      messageId: dernier.messageId,
      payload: { spec: dernier.spec, documentId, version: null },
      silencieux: true,
    });
  };

  // Réponse qui produit ou modifie un document : le panneau s'ouvre dessus (écran large), le
  // dernier si plusieurs ; il montre la mise à jour, puis la nouvelle version
  const versionsVues = useRef(new Map());
  useEffect(() => {
    const avant = versionsVues.current;
    let change = null;
    for (const [id, liste] of documents) {
      const derniere = liste.at(-1).version;
      if (!avant.has(id) || derniere > avant.get(id)) change = id;
      avant.set(id, derniere);
    }
    if (!wide || !streaming) return;
    const cible = change ?? (enCours ? [...documents.keys()].at(-1) : null);
    if (cible && openId !== panneauDocument(cible)) ouvrir(cible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, enCours, wide, streaming, openId]);

  // Nouvelle conversation : plus de sélection
  useEffect(() => {
    if (documents.size === 0) selectionner(null);
  }, [documents.size]);

  return useMemo(() => ({ documents, enCours, selection, selectionner }), [documents, enCours, selection]);
}
